import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SheetGrid, { SheetRow } from './viewers/SheetGrid';
import Spinner from '../../shared/TailwindComponents/Spinner';
import Button from '../../shared/TailwindComponents/Button';

export type SheetMap = Record<string, SheetRow[]>;

interface ExcelEditorProps {
  csvContent?: string;
  blobUrl?: string;
  /** CSV of the ACTIVE sheet - the shape SecureFileViewer's save path expects. */
  onContentChange: (content: string) => void;
  /** Every sheet, in workbook order, so a multi-sheet save keeps the others. */
  onSheetsChange?: (sheets: SheetMap, order: string[]) => void;
  fileName: string;
  onDataLoaded?: (data: any[]) => void;
  isLoading?: boolean;
  /** Save the workbook. Shown as a button once a cell has been edited. */
  onSave?: () => void | Promise<void>;
  /** Reading zoom factor, 1 = 100%. */
  zoom?: number;
}

/** Quote-and-escape a row the way a CSV reader expects to find it. */
const toCsv = (rows: SheetRow[]): string =>
  rows
    .map((row) =>
      (Array.isArray(row) ? row : [row])
        .map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`)
        .join(',')
    )
    .join('\n');

/**
 * The spreadsheet surface in the file viewer.
 *
 * Reading and editing are ONE view (files-18): the grid renders the sheet and
 * the same grid takes the edits. The raw-CSV textarea it used to swap in is
 * gone, along with the "Edit CSV" toggle that opened it.
 */
const ExcelEditor: React.FC<ExcelEditorProps> = ({
  csvContent,
  blobUrl,
  onContentChange,
  onSheetsChange,
  onDataLoaded,
  isLoading = false,
  onSave,
  zoom = 1,
}) => {
  const { t } = useTranslation();
  const [sheets, setSheets] = useState<SheetMap>({});
  const [sheetOrder, setSheetOrder] = useState<string[]>([]);
  const [activeSheet, setActiveSheet] = useState<string>('');
  const [isLoadingSheet, setIsLoadingSheet] = useState(false);
  const [error, setError] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const onContentChangeRef = useRef(onContentChange);
  const onSheetsChangeRef = useRef(onSheetsChange);
  onContentChangeRef.current = onContentChange;
  onSheetsChangeRef.current = onSheetsChange;

  const publish = useCallback((next: SheetMap, order: string[], active: string) => {
    onContentChangeRef.current(toCsv(next[active] || []));
    onSheetsChangeRef.current?.(next, order);
  }, []);

  // Load the workbook from the decrypted blob
  useEffect(() => {
    if (!blobUrl) return;
    let cancelled = false;

    const loadExcelFromBlob = async () => {
      try {
        setIsLoadingSheet(true);
        setError('');

        const response = await fetch(blobUrl);
        if (!response.ok) throw new Error('Failed to fetch file');

        const arrayBuffer = await response.arrayBuffer();

        let loaded: SheetMap;
        let order: string[];
        try {
          const XLSX = await import('@e965/xlsx');
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          loaded = {};
          order = [...workbook.SheetNames];
          workbook.SheetNames.forEach((sheetName: string) => {
            loaded[sheetName] = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 }) as SheetRow[];
          });
        } catch (xlsxError) {
          // Fallback: treat as CSV
          const text = new TextDecoder().decode(arrayBuffer);
          const rows = text.split('\n').map((row) => row.split(',').map((cell) => cell.trim()));
          loaded = { Data: rows };
          order = ['Data'];
        }

        if (cancelled) return;
        const first = order[0] || '';
        setSheets(loaded);
        setSheetOrder(order);
        setActiveSheet(first);
        setHasChanges(false);
        if (onDataLoaded) onDataLoaded(loaded[first] || []);
        publish(loaded, order, first);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading Excel from blob:', err);
        setError(err instanceof Error ? err.message : t('Failed to load spreadsheet'));
      } finally {
        if (!cancelled) setIsLoadingSheet(false);
      }
    };

    loadExcelFromBlob();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobUrl]);

  // Sync CSV content from props (no blob - content handed in directly)
  useEffect(() => {
    if (!csvContent || blobUrl) return;
    const rows = csvContent
      .split('\n')
      .filter((line) => line.trim())
      .map((line) => line.split(',').map((cell) => cell.trim()));
    setSheets({ Sheet1: rows });
    setSheetOrder(['Sheet1']);
    setActiveSheet('Sheet1');
    setHasChanges(false);
  }, [csvContent, blobUrl]);

  const handleRowsChange = useCallback(
    (rows: SheetRow[]) => {
      const next = { ...sheets, [activeSheet]: rows };
      setSheets(next);
      setHasChanges(true);
      publish(next, sheetOrder, activeSheet);
    },
    [sheets, activeSheet, sheetOrder, publish]
  );

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    try {
      setIsSaving(true);
      await onSave();
      setHasChanges(false);
    } finally {
      setIsSaving(false);
    }
  }, [onSave]);

  // Cmd/Ctrl+S saves, the way it does in the document editor.
  useEffect(() => {
    if (!onSave) return;
    const handler = (e: KeyboardEvent) => {
      const modKey = navigator.platform.toUpperCase().includes('MAC') ? e.metaKey : e.ctrlKey;
      if (modKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onSave, handleSave]);

  if (isLoadingSheet || isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-bg">
        <div className="text-center">
          <Spinner size="sm" className="mb-4" />
          <p className="text-fg-muted text-sm">{t('Loading spreadsheet...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-bg">
        <div className="text-center">
          <p className="text-danger-600 dark:text-danger-400 mb-2">{t('Error loading spreadsheet')}</p>
          <p className="text-sm text-fg-muted">{error}</p>
        </div>
      </div>
    );
  }

  const currentSheetData = sheets[activeSheet] || [];

  return (
    <div className="w-full flex flex-col bg-bg min-h-full">
      {/* Sheet tabs + save. The viewer owns the scrolling, so this bar sits
          at the top of the document region rather than sticking to a scroller. */}
      {(sheetOrder.length > 1 || onSave) && (
        <div className="flex-shrink-0 glass-bar border-b border-hairline px-4 py-1.5 flex items-center justify-between gap-4 sticky top-0 z-10">
          <div className="flex gap-1 overflow-x-auto">
            {sheetOrder.length > 1 &&
              sheetOrder.map((sheetName) => (
                <button
                  key={sheetName}
                  onClick={() => {
                    setActiveSheet(sheetName);
                    publish(sheets, sheetOrder, sheetName);
                  }}
                  className={activeSheet === sheetName ? 'nav-pill nav-pill-active' : 'nav-pill'}
                >
                  {sheetName}
                </button>
              ))}
          </div>
          {onSave && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {hasChanges && <span className="text-[11px] text-fg-muted">{t('Unsaved changes')}</span>}
              <Button
                variant="primary"
                size="small"
                onClick={handleSave}
                disabled={!hasChanges}
                isLoading={isSaving}
              >
                {t('Save')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* The grid scrolls sideways only; the page's own bar handles the rest. */}
      <div className="w-full overflow-x-auto custom-scrollbar-main">
        <SheetGrid
          rows={currentSheetData}
          onChange={onSave ? handleRowsChange : undefined}
          showHeaders
          zoom={zoom}
        />
      </div>
    </div>
  );
};

export default ExcelEditor;
